import type { JourneyLeg, LatLon } from '@/libs/journey/journeys';

/** Where the traveller stands on the journey. */
export interface JourneyProgress {
  /** Index of the leg under way, `legs.length` once the journey is over. */
  leg: number;
  /** On the ride under way, index in its `stopIds` of the last stop called at: -1 before boarding. */
  lastStop: number;
  /** The position is known and lies on the leg under way: the timeline shows it. */
  located: boolean;
}

export type StopState = 'default' | 'past';
export type SegmentState = StopState | 'current';

export type TimelineRow =
  | { key: string; kind: 'origin'; state: StopState }
  | { key: string; kind: 'walk'; leg: number; state: SegmentState; transfer: boolean }
  | { key: string; kind: 'board'; leg: number; state: StopState }
  | { key: string; kind: 'ride'; leg: number; state: SegmentState; boarded: boolean; expanded: boolean }
  | { key: string; kind: 'stop'; leg: number; stop: number; state: StopState }
  | { key: string; kind: 'position'; leg: number; nextStop: number }
  | { key: string; kind: 'alight'; leg: number; state: StopState; final: boolean }
  | { key: string; kind: 'destination'; state: StopState };

// GPS accuracy in town, and the slack between a stop and the road it stands by.
const MAX_OFF_ROUTE_M = 150;
const STOP_SLACK_M = 20;
const METERS_PER_DEGREE = 111_320;

type Point = [lon: number, lat: number];

/** Equirectangular metres around `lat0`: precise enough over the length of a ride. */
function toMeters([lon, lat]: Point, lat0: number): [number, number] {
  return [lon * METERS_PER_DEGREE * Math.cos((lat0 * Math.PI) / 180), lat * METERS_PER_DEGREE];
}

/** Distance along the path to the point's foot on it, and how far the point lies from the path, in metres. */
function alongPath(path: Point[], point: Point): { along: number; offset: number } {
  const lat0 = point[1];
  const [px, py] = toMeters(point, lat0);
  let walked = 0;
  let best = { along: 0, offset: Infinity };
  for (let i = 1; i < path.length; i++) {
    const [ax, ay] = toMeters(path[i - 1], lat0);
    const [bx, by] = toMeters(path[i], lat0);
    const [dx, dy] = [bx - ax, by - ay];
    const length = Math.hypot(dx, dy);
    const t = length > 0 ? Math.min(1, Math.max(0, ((px - ax) * dx + (py - ay) * dy) / length ** 2)) : 0;
    const offset = Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
    if (offset < best.offset) best = { along: walked + t * length, offset };
    walked += length;
  }
  return best;
}

/**
 * The leg under way comes from the clock, on the live times of the journey. On a ride, the
 * phone's position placed on the ride's path tells the last stop called at; without it, or
 * too far from the path to trust, the times tell it.
 */
export function journeyProgress(
  legs: JourneyLeg[],
  now: number,
  position: LatLon | undefined,
  stopPoint: (stopId: string) => LatLon | undefined,
): JourneyProgress {
  const leg = legs.findIndex(candidate => now < candidate.arrival);
  if (leg === -1) return { leg: legs.length, lastStop: -1, located: false };

  const current = legs[leg];
  if (current.type === 'walk') return { leg, lastStop: -1, located: position !== undefined };
  if (now < current.departure) return { leg, lastStop: -1, located: false };

  // The ride ends by the clock: until then the traveller is short of the alighting stop.
  const lastCalled = current.stopIds.length - 2;
  // The times only grow along a ride: those behind the clock are the stops called at.
  const byTime = Math.min(lastCalled, Math.max(0, current.stopTimes.filter(time => time <= now).length - 1));
  if (!position) return { leg, lastStop: byTime, located: false };

  const placed = alongPath(current.path, [position.lon, position.lat]);
  if (placed.offset > MAX_OFF_ROUTE_M) return { leg, lastStop: byTime, located: false };

  let reached = 0;
  let previous = 0;
  current.stopIds.forEach((stopId, index) => {
    const point = stopPoint(stopId);
    if (!point) return;
    previous = Math.max(previous, alongPath(current.path, [point.lon, point.lat]).along);
    if (previous <= placed.along + STOP_SLACK_M) reached = index;
  });
  return { leg, lastStop: Math.min(lastCalled, reached), located: true };
}

/**
 * The rows of the timeline, in order: the stops where something happens, the rides and walks
 * between them, and on an unfolded ride the stops it calls at and the traveller among them.
 */
export function timelineRows(
  legs: JourneyLeg[],
  progress: JourneyProgress,
  expanded: ReadonlySet<number>,
): TimelineRow[] {
  const rows: TimelineRow[] = [];
  const over = progress.leg >= legs.length;
  if (legs[0]?.type === 'walk')
    rows.push({ key: 'origin', kind: 'origin', state: progress.leg > 0 ? 'past' : 'default' });

  legs.forEach((leg, index) => {
    const done = index < progress.leg;
    const underWay = index === progress.leg;
    if (leg.type === 'walk') {
      rows.push({
        key: `walk-${index}`,
        kind: 'walk',
        leg: index,
        state: done ? 'past' : underWay && progress.located ? 'current' : 'default',
        transfer: legs[index - 1]?.type === 'transit' && legs[index + 1]?.type === 'transit',
      });
      if (index === legs.length - 1)
        rows.push({ key: 'destination', kind: 'destination', state: over ? 'past' : 'default' });
      return;
    }

    const boarded = underWay && progress.lastStop >= 0;
    const located = boarded && progress.located;
    const isExpanded = expanded.has(index) && leg.stopIds.length > 2;
    rows.push({
      key: `board-${index}`,
      kind: 'board',
      leg: index,
      state: done || boarded ? 'past' : 'default',
    });
    rows.push({
      key: `ride-${index}`,
      kind: 'ride',
      leg: index,
      // Unfolded, the stops called at carry the progress: the ride itself is behind.
      state: done || (boarded && isExpanded) ? 'past' : located ? 'current' : 'default',
      boarded: done || boarded,
      expanded: isExpanded,
    });
    if (isExpanded) {
      const lastIndex = leg.stopIds.length - 1;
      for (let stop = 1; stop <= lastIndex; stop++) {
        if (located && stop === progress.lastStop + 1) {
          rows.push({ key: `position-${index}`, kind: 'position', leg: index, nextStop: stop });
        }
        if (stop === lastIndex) break;
        rows.push({
          key: `stop-${index}-${stop}`,
          kind: 'stop',
          leg: index,
          stop,
          state: done || (boarded && stop <= progress.lastStop) ? 'past' : 'default',
        });
      }
    }
    rows.push({
      key: `alight-${index}`,
      kind: 'alight',
      leg: index,
      state: done ? 'past' : 'default',
      final: index === legs.length - 1,
    });
  });
  return rows;
}
